/**
 * Roof Scanner Plugin - Main Entry Point
 * Barebones embeddable widget with two trigger modes:
 * 1. Overlay button (automatic)
 * 2. Data attribute trigger (manual)
 * 
 * Now using page-based architecture with Page1.js and Page2.js
 */

// Import page classes - will be bundled at build time
import Page1 from './pages/Page1.js';
import Page2 from './pages/Page2.js';
import Page3 from './pages/Page3.js';
import Page4 from './pages/Page4.js';
import Page5 from './pages/Page5.js';
import PageFallthrough from './pages/PageFallthrough.js';

// Import centralized configuration
import { SERVER_URL, API_ENDPOINTS, GOOGLE_MAPS_API_KEY } from './config.js';

// Import logger for error tracking
import { logger, FIELDS } from './utils/logger.js';

// Set up pages immediately - no async loading needed
if (!window.RoofScannerPages) {
  window.RoofScannerPages = {};
}
window.RoofScannerPages.Page1 = Page1;
window.RoofScannerPages.Page2 = Page2;
window.RoofScannerPages.Page3 = Page3;
window.RoofScannerPages.Page4 = Page4;
window.RoofScannerPages.Page5 = Page5;
window.RoofScannerPages.PageFallthrough = PageFallthrough;

// Prevent multiple loads
if (!window.__roofScannerLoaded) {
window.__roofScannerLoaded = true;

class RoofScannerWidget {
  constructor() {
    this.config = {};
    this.isOpen = false;
    this.container = null;
    this.shadowRoot = null;
    this.triggerButton = null;
    
    // Page management
    this.pages = {};
    this.currentPage = 1;
    
    // Global session object for cross-page data sharing
    this.session = this.initializeSession();
  }

  initializeSession() {
    return {
      // Empty session object - properties can be added as needed
    };
  }

  // Session helper methods for cross-page data sharing
  getSessionData(key) {
    return this.session[key];
  }

  setSessionData(key, value) {
    this.session[key] = value;
  }

  clearSessionData(key) {
    if (key) {
      delete this.session[key];
    } else {
      // Clear all session data
      this.session = this.initializeSession();
    }
  }

  async init(config = {}) {

    // Extract API key from script src URL
    const apiKey = this.extractApiKeyFromScript();

    // Require API key to proceed
    if (!apiKey) {
      logger.error({
        [FIELDS.OPERATION]: 'init',
        [FIELDS.REASON]: 'missing_api_key'
      }, 'Widget initialization failed - API key is required');
      this.showAPIKeyError();
      return;
    }

    // Store only API key - everything else comes from backend
    this.config = {
      apiKey: apiKey
    };

    // Load configuration from backend asynchronously
    await this.loadConfiguration();

    // Pages are already loaded via static imports

    // Create the widget container (this calls showPage(1))
    this.createContainer();

    // Setup triggers based on mode
    if (this.config.mode === 'overlay') {
      this.createOverlayButton();
    }
    
    // Always setup data-attribute triggers
    this.setupDataAttributeTriggers();

  }

  extractApiKeyFromScript() {
    // Find the current script tag or the one with roof-scanner in the src
    const scriptTag = document.currentScript ||
                     document.querySelector('script[src*="roof-scanner"]') ||
                     document.querySelector('script[src*="main.js"]');

    if (!scriptTag || !scriptTag.src) {
      logger.error({
        [FIELDS.OPERATION]: 'extractApiKey',
        [FIELDS.REASON]: 'script_tag_not_found'
      }, 'Could not find script tag to extract API key');
      return null;
    }

    try {
      const url = new URL(scriptTag.src);
      const apiKey = url.searchParams.get('key');
      if (!apiKey) {
        logger.error({
          [FIELDS.OPERATION]: 'extractApiKey',
          [FIELDS.REASON]: 'missing_key_parameter'
        }, 'No API key provided in script URL - use ?key=YOUR_API_KEY');
        return null;
      }
      return apiKey;
    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'extractApiKey'
      }, 'Error extracting API key from script URL');
      return null;
    }
  }

  async loadConfiguration() {

    // Use centralized server URL from config
    const configUrl = `${SERVER_URL}${API_ENDPOINTS.CONFIG}?apiKey=${encodeURIComponent(this.config.apiKey)}`;
    const response = await fetch(configUrl);

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success || !data.config) {
      throw new Error('Invalid config response from server');
    }

    // Merge embedded config with server config
    this.config = {
      // Embedded in plugin (from build-time environment variables)
      apiKey: this.config.apiKey,
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      serverUrl: SERVER_URL,

      // From server (organization-specific widget configuration)
      ...data.config
    };

    // Expose minimal config globally for logger and API helpers
    // Contains: serverUrl, widgetToken (JWT), organizationId
    window.ROOF_SCANNER_CONFIG = {
      serverUrl: this.config.serverUrl,
      widgetToken: this.config.widgetToken,
      organizationId: this.config.organizationId
    };

  }

  createContainer() {
    // Create custom element for Shadow DOM isolation
    if (!customElements.get('roof-scanner-widget')) {
      customElements.define('roof-scanner-widget', class extends HTMLElement {
        constructor() {
          super();
          this.attachShadow({ mode: 'open' });
        }
      });
    }

    // Create and append widget container
    this.container = document.createElement('roof-scanner-widget');
    document.body.appendChild(this.container);
    this.shadowRoot = this.container.shadowRoot;

    // Initialize pages (will be loaded dynamically)
    this.pages = {};
    
    // Setup Shadow DOM content
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 2147483647;
          display: none;
        }

        :host([data-open="true"]) {
          display: block;
        }

        .backdrop {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          pointer-events: auto;
        }

        .modal {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100vh;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 0;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          pointer-events: auto;
          padding: 20px;
          overflow-y: auto;
        }

        /* On medium screens (tablets), add slight margins */
        @media (min-width: 768px) {
          .modal {
            top: 2vh;
            left: 2vw;
            width: 96vw;
            height: 96vh;
            border-radius: 12px;
          }
        }

        /* On large screens (14" laptop and up), constrain to reasonable size */
        @media (min-width: 1366px) and (min-height: 768px) {
          .modal {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 90%;
            max-width: 1000px;
            height: auto;
            max-height: 95vh;
            border-radius: 16px;
          }
        }

        .close-button {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border: none;
          color: white;
          font-size: 18px;
          font-weight: 400;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
          overflow: hidden;
          z-index: 10;
        }

        .close-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          transition: left 0.3s ease;
          z-index: 0;
        }

        .close-button:hover::before {
          left: 0;
        }

        .close-button:hover {
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          transform: translateY(-1px);
        }

        .close-button:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
        }

        .close-button-icon {
          position: relative;
          z-index: 1;
          line-height: 1;
        }

        .content {
          padding: 20px;
        }

        /* Page 1 Header Styles */
        .page-header {
          padding: 0 0 24px 0;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 20px;
        }

        .page-title {
          font-size: 20px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 8px 0;
        }

        .page-subtitle {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          margin: 0 0 16px 0;
        }

        /* Step Indicator */
        .step-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .step {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #9ca3af;
          transition: all 0.3s ease;
        }

        .step.active {
          color: #6366f1;
        }

        .step.completed {
          color: #10b981;
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 600;
          background: white;
          transition: all 0.3s ease;
        }

        .step.active .step-number {
          border-color: #6366f1;
          background: #6366f1;
          color: white;
        }

        .step.completed .step-number {
          border-color: #10b981;
          background: #10b981;
          color: white;
        }

        .step-text {
          font-weight: 500;
        }

        .step-divider {
          width: 40px;
          height: 2px;
          background: #e5e7eb;
          transition: all 0.3s ease;
        }

        .step-divider.completed {
          background: #10b981;
        }

        /* Debug Section */
        .debug-section {
          background: #f9fafb;
          padding: 12px;
          margin-top: 16px;
          font-family: monospace;
          font-size: 11px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          color: #9ca3af;
          opacity: 0.8;
        }

        /* Search Section */
        .search-section {
          padding: 20px 40px 0 40px;
          margin: 0 -40px;
        }

        .search-container {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 12px 48px 12px 48px;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
        }

        .search-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .search-input::placeholder {
          color: #9ca3af;
        }

        .loading-indicator {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #6366f1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .suggestions-dropdown {
          position: absolute;
          width: 100%;
          max-height: 200px;
          overflow-y: auto;
          background: white;
          border: 2px solid #e5e7eb;
          border-top: none;
          border-radius: 0 0 12px 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 20;
          display: none;
          top: 100%;
        }

        .suggestion-item {
          padding: 12px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          font-size: 14px;
          transition: background-color 0.2s ease;
        }

        .suggestion-item:last-child {
          border-bottom: none;
        }

        .suggestion-item:hover {
          background-color: #f9fafb;
        }

        .error-suggestion {
          color: #9ca3af;
          cursor: default;
          font-style: italic;
        }

        .error-suggestion:hover {
          background-color: transparent;
        }

        .instruction-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          font-size: 13px;
          color: #92400e;
          margin-top: 12px;
        }

        .instruction-icon {
          width: 16px;
          height: 16px;
        }

        .error-message {
          color: #d32f2f;
          background-color: #ffebee;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 16px;
          font-size: 14px;
        }


        /* Map Section */
        .map-container {
          position: relative;
          width: calc(100% + 80px);
          height: 320px;
          background: #f3f4f6;
          overflow: hidden;
          border-radius: 0;
          margin: 20px -40px;
          border: none;
          border-top: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }

        .map-placeholder {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f3f4f6 25%, #e5e7eb 100%);
          z-index: 1;
          transition: all 0.5s ease;
        }

        .map-placeholder.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .placeholder-icon {
          width: 64px;
          height: 64px;
          color: #9ca3af;
          margin-bottom: 16px;
        }

        .placeholder-text {
          font-size: 16px;
          font-weight: 500;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .placeholder-subtext {
          font-size: 14px;
          color: #9ca3af;
        }

        .helper-text {
          position: absolute;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.9);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          z-index: 20;
          pointer-events: none;
          opacity: 0;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          border: 2px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }

        .helper-text.visible {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }

        /* Center Map Pin */
        .center-pin-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -100%);
          z-index: 15;
          pointer-events: none;
          display: none;
          filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2));
          animation: pinBounce 0.5s ease;
        }

        .center-pin-overlay.visible {
          display: block;
        }

        @keyframes pinBounce {
          0%, 100% { transform: translate(-50%, -100%); }
          50% { transform: translate(-50%, -105%); }
        }

        .map-pin-svg {
          width: 40px;
          height: 40px;
        }

        /* Location pulse effect */
        .location-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 24px;
          height: 24px;
          background: rgba(239, 68, 68, 0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          animation: locationPulse 2s infinite;
          z-index: 14;
          pointer-events: none;
          display: none;
        }

        .location-pulse.visible {
          display: block;
        }

        @keyframes locationPulse {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }

        /* Location center dot */
        .location-dot {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 12px;
          height: 12px;
          background: #ef4444;
          border: 2px solid white;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          z-index: 16;
          pointer-events: none;
          display: none;
        }

        .location-dot.visible {
          display: block;
        }

        #map {
          width: 100%;
          height: 100%;
        }

        /* Footer Section */
        .page-footer {
          padding: 20px 40px;
          background: #fafafa;
          border-radius: 8px;
          margin: 20px -40px 0 -40px;
        }

        .location-preview {
          display: none;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
          padding: 12px;
          background: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          animation: slideUp 0.3s ease;
        }

        .location-preview.visible {
          display: flex;
        }

        @keyframes slideUp {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .location-icon {
          width: 20px;
          height: 20px;
          color: #6366f1;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .location-details {
          flex: 1;
        }

        .location-address {
          font-size: 14px;
          color: #111827;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .location-coords {
          font-size: 12px;
          color: #6b7280;
        }

        .confirm-btn {
          width: 100%;
          padding: 14px 24px;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
          position: relative;
          overflow: hidden;
        }

        .confirm-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          transition: left 0.3s ease;
          z-index: 0;
        }

        .confirm-btn span {
          position: relative;
          z-index: 1;
        }

        .confirm-btn:hover:not(:disabled)::before {
          left: 0;
        }

        .confirm-btn:hover:not(:disabled) {
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
        }

        .confirm-btn:active:not(:disabled) {
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .confirm-btn:disabled {
          background: #e5e7eb;
          cursor: not-allowed;
          box-shadow: none;
        }

        .confirm-btn:disabled::before {
          display: none;
        }

        /* Page System */
        .page {
          width: 100%;
        }

        /* Page 2 Styles */
        .analysis-content {
          padding: 20px 0;
        }

        .selected-location-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #4CAF50;
        }

        .coordinates-display {
          font-family: monospace;
          font-size: 14px;
          color: #333;
          background: white;
          padding: 8px;
          border-radius: 4px;
          margin-top: 5px;
        }

        .results-placeholder {
          text-align: center;
          padding: 40px 20px;
          background: #f9f9f9;
          border-radius: 8px;
          margin: 20px 0;
        }

        .loading-animation {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .spinner-large {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .page-controls {
          margin-top: 30px;
          text-align: center;
        }

        .back-button {
          background: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          font-size: 14px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .back-button:active {
          transform: translateY(0);
        }

        /* Results Display Styles */
        .results-content {
          text-align: left;
        }

        .results-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin: 20px 0;
        }

        .result-card {
          background: white;
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .result-card.full-width {
          grid-column: 1 / -1;
        }

        .result-card h4 {
          margin: 0 0 10px 0;
          color: #2c3e50;
          font-size: 16px;
        }

        .result-card p {
          margin: 5px 0;
          font-size: 14px;
          color: #555;
        }

        .config-item {
          background: #f8f9fa;
          padding: 8px;
          margin: 5px 0;
          border-radius: 4px;
          font-size: 14px;
        }

        .error-state {
          text-align: center;
          padding: 30px;
          color: #d32f2f;
        }

        .retry-button {
          background: #ff9800;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 10px;
        }

        .retry-button:hover {
          background: #f57c00;
        }

        @media (max-width: 600px) {
          .results-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Confirmation Popup Styles */
        .confirmation-popup {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 2147483648;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
        }

        .confirmation-backdrop {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(2px);
        }

        .confirmation-dialog {
          position: relative;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          max-width: 400px;
          width: 90%;
          animation: confirmationSlideIn 0.3s ease;
        }

        @keyframes confirmationSlideIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .confirmation-content {
          padding: 24px;
          text-align: center;
        }

        .confirmation-title {
          margin: 0 0 12px 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .confirmation-message {
          margin: 0 0 24px 0;
          font-size: 16px;
          color: #6b7280;
          line-height: 1.5;
        }

        .confirmation-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .confirmation-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .cancel-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .cancel-btn:hover {
          background: #e5e7eb;
          transform: translateY(-1px);
        }

        .abandon-confirm-btn {
          background: #dc2626;
          color: white;
        }

        .confirmation-btn:active {
          transform: translateY(0);
        }

      </style>
      <div class="backdrop"></div>
      <div class="modal">
        <button class="close-button" aria-label="Close">
          <span class="close-button-icon">×</span>
        </button>
        <div class="content" id="page-container">
          <!-- Page content will be dynamically loaded here -->
        </div>
      </div>
      <!-- Confirmation popup -->
      <div class="confirmation-popup" id="abandon-confirmation" style="display: none;">
        <div class="confirmation-backdrop"></div>
        <div class="confirmation-dialog">
          <div class="confirmation-content">
            <h3 class="confirmation-title">Abandon Analysis?</h3>
            <p class="confirmation-message">This will discard your ongoing roof analysis. Are you sure you want to close?</p>
            <div class="confirmation-buttons">
              <button class="confirmation-btn cancel-btn" id="nevermind-btn">Nevermind</button>
              <button class="confirmation-btn abandon-confirm-btn" id="confirm-abandon-btn">Close</button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Setup close handlers
    const backdrop = this.shadowRoot.querySelector('.backdrop');
    const closeButton = this.shadowRoot.querySelector('.close-button');

    // Removed backdrop click handler - modal won't close when clicking outside
    closeButton.addEventListener('click', () => this.closeWidget());

    // Handle ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeWidget();
      }
    });

    // Note: Confirmation popup handlers are set up when first shown

    // Initialize pagination - start on page 1
    this.showPage(1);
  }

  // Page Management System
  async showPage(pageNumber, data = {}) {
    
    try {
      // Unmount current page
      if (this.currentPage && this.pages[this.currentPage]) {
        this.pages[this.currentPage].onUnmount();
      }
      
      // Get page container
      const pageContainer = this.shadowRoot.querySelector('#page-container');
      if (!pageContainer) {
        logger.error({
          [FIELDS.OPERATION]: 'showPage',
          [FIELDS.PAGE_NUMBER]: pageNumber,
          [FIELDS.REASON]: 'container_not_found'
        }, 'Page container not found in shadow DOM');
        return;
      }
      
      // Create page if not already loaded
      if (!this.pages[pageNumber]) {
        // Direct class instantiation using global namespace
        if (pageNumber === 1 && window.RoofScannerPages?.Page1) {
          this.pages[pageNumber] = new window.RoofScannerPages.Page1(this);
        } else if (pageNumber === 2 && window.RoofScannerPages?.Page2) {
          this.pages[pageNumber] = new window.RoofScannerPages.Page2(this);
        } else if (pageNumber === 3 && window.RoofScannerPages?.Page3) {
          this.pages[pageNumber] = new window.RoofScannerPages.Page3(this);
        } else if (pageNumber === 4 && window.RoofScannerPages?.Page4) {
          this.pages[pageNumber] = new window.RoofScannerPages.Page4(this);
        } else if (pageNumber === 5 && window.RoofScannerPages?.Page5) {
          this.pages[pageNumber] = new window.RoofScannerPages.Page5(this);
        } else if (pageNumber === 'fallthrough' && window.RoofScannerPages?.PageFallthrough) {
          this.pages[pageNumber] = new window.RoofScannerPages.PageFallthrough(this);
        } else {
          logger.error({
            [FIELDS.OPERATION]: 'showPage',
            [FIELDS.PAGE_NUMBER]: pageNumber,
            [FIELDS.REASON]: 'page_class_not_found'
          }, `Page ${pageNumber} not found or not loaded`);
          return;
        }
      }
      
      // Load new page
      const page = this.pages[pageNumber];
      if (page) {
        // Inject page-specific CSS if available
        if (page.getCSS && typeof page.getCSS === 'function') {
          const existingPageStyle = this.shadowRoot.querySelector('#page-styles');
          if (existingPageStyle) {
            existingPageStyle.remove();
          }

          const pageStyleElement = document.createElement('style');
          pageStyleElement.id = 'page-styles';
          pageStyleElement.textContent = page.getCSS();
          this.shadowRoot.appendChild(pageStyleElement);
        }

        // Render page content
        pageContainer.innerHTML = page.render();

        // Mount the page
        page.onMount(data);

        // Update current page
        this.currentPage = pageNumber;

      } else {
        logger.error({
          [FIELDS.OPERATION]: 'showPage',
          [FIELDS.PAGE_NUMBER]: pageNumber,
          [FIELDS.REASON]: 'page_instance_not_found'
        }, `Page ${pageNumber} not found after creation`);
      }
    } catch (error) {
      logger.error({
        [FIELDS.ERROR]: error,
        [FIELDS.OPERATION]: 'showPage',
        [FIELDS.PAGE_NUMBER]: pageNumber
      }, `Error loading page ${pageNumber}`);
    }
  }

  createOverlayButton() {

    // Create trigger button
    this.triggerButton = document.createElement('button');
    this.triggerButton.setAttribute('aria-label', 'Get Instant Quote');
    this.triggerButton.textContent = 'Get Instant Quote';

    // Mobile detection
    const isMobile = () => {
      return window.innerWidth <= 768 ||
             /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const isMobileDevice = isMobile();
    const mobileConfig = this.config.mobile;

    // Early exit if hideOnMobile is true
    if (isMobileDevice && mobileConfig?.hideOnMobile) {
      return;
    }

    // Choose position configuration based on device
    const positionConfig = isMobileDevice && mobileConfig ? {
      vertical: mobileConfig.position?.vertical || 'bottom',
      horizontal: mobileConfig.position?.horizontal || 'center',
      offsetX: mobileConfig.offsetX || 16,
      offsetY: mobileConfig.offsetY || 16
    } : this.config.position || {
      vertical: 'bottom',
      horizontal: 'right',
      offsetX: 24,
      offsetY: 24
    };

    // Calculate position styles dynamically
    const calculatePosition = (vertical, horizontal, offsetX, offsetY) => {
      const styles = {};
      let transform = '';

      // Vertical positioning
      if (vertical === 'top') {
        styles.top = `${offsetY}px`;
        styles.bottom = 'auto';
      } else if (vertical === 'center') {
        styles.top = '50%';
        styles.bottom = 'auto';
        transform += 'translateY(-50%) ';
      } else {
        styles.bottom = `${offsetY}px`;
        styles.top = 'auto';
      }

      // Horizontal positioning
      if (horizontal === 'left') {
        styles.left = `${offsetX}px`;
        styles.right = 'auto';
      } else if (horizontal === 'center') {
        styles.left = '50%';
        styles.right = 'auto';
        transform += 'translateX(-50%)';
      } else {
        styles.right = `${offsetX}px`;
        styles.left = 'auto';
      }

      if (transform) {
        styles.transform = transform.trim();
      }

      return styles;
    };

    const pos = calculatePosition(
      positionConfig.vertical,
      positionConfig.horizontal,
      positionConfig.offsetX,
      positionConfig.offsetY
    );

    // Theme-based colors with fallbacks
    const primaryColor = this.config.theme?.primaryColor || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
    const textColor = this.config.theme?.textColor === 'dark' ? '#1f2937' : '#ffffff';

    // Apply styles
    Object.assign(this.triggerButton.style, {
      position: 'fixed',
      ...pos,
      padding: '14px 24px',
      borderRadius: '12px',
      background: primaryColor,
      border: 'none',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
      cursor: 'pointer',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      color: textColor,
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    });

    // Create hover background element (pseudo-element effect)
    const hoverBg = document.createElement('div');
    hoverBg.style.cssText = `
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
      transition: left 0.3s ease;
      z-index: 0;
    `;
    this.triggerButton.appendChild(hoverBg);

    // Create text span for z-index layering
    const textSpan = document.createElement('span');
    textSpan.textContent = 'Get Instant Quote';
    textSpan.style.cssText = `
      position: relative;
      z-index: 1;
    `;
    this.triggerButton.textContent = '';
    this.triggerButton.appendChild(textSpan);

    // Add hover effects
    this.triggerButton.addEventListener('mouseenter', () => {
      hoverBg.style.left = '0';
      this.triggerButton.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)';
    });

    this.triggerButton.addEventListener('mouseleave', () => {
      hoverBg.style.left = '-100%';
      this.triggerButton.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
    });

    this.triggerButton.addEventListener('mousedown', () => {
      this.triggerButton.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
    });

    // Add click handler
    this.triggerButton.addEventListener('click', () => this.open());

    // Setup mobile scroll hide behavior
    if (isMobileDevice && mobileConfig?.hideOnScroll) {
      this.setupScrollHideListener();
    }

    // Append to body
    document.body.appendChild(this.triggerButton);
  }

  setupScrollHideListener() {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateButtonVisibility = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY && currentScrollY > 50; // 50px threshold

      if (this.triggerButton) {
        // Preserve existing transform for centering, add scroll transform
        const existingTransform = this.triggerButton.style.transform || '';
        const baseTransform = existingTransform.replace(/translateY\([^)]*\)/g, '').trim();

        this.triggerButton.style.transform = isScrollingDown
          ? `${baseTransform} translateY(100px)`.trim() // Hide by moving down
          : baseTransform; // Show by removing scroll transform
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateButtonVisibility);
        ticking = true;
      }
    }, { passive: true });

  }

  setupDataAttributeTriggers() {

    // Use event delegation for efficiency
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-roof-scanner-trigger]');
      if (trigger) {
        event.preventDefault();
        this.open();
      }
    });
  }

  open() {
    if (this.isOpen) return;
    
    
    // Reset session for fresh start on each modal open
    this.clearSessionData();
    
    // Reset to Page 1 for fresh start
    this.showPage(1);
    
    this.isOpen = true;
    this.container.setAttribute('data-open', 'true');
    
    // Hide trigger button when modal is open
    if (this.triggerButton) {
      this.triggerButton.style.display = 'none';
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.container.removeAttribute('data-open');

    // Show trigger button when modal is closed
    if (this.triggerButton) {
      this.triggerButton.style.display = 'flex';
    }

    // Restore body scroll
    document.body.style.overflow = '';
  }

  closeWidget(skipConfirmation = false) {
    if (!this.isOpen) return;

    if (skipConfirmation) {
      this.close();
    } else {
      this.showAbandonConfirmation();
    }
  }

  setupAbandonConfirmation() {
    const confirmationPopup = this.shadowRoot.querySelector('#abandon-confirmation');
    const nevermindBtn = this.shadowRoot.querySelector('#nevermind-btn');
    const confirmAbandonBtn = this.shadowRoot.querySelector('#confirm-abandon-btn');
    const confirmationBackdrop = this.shadowRoot.querySelector('.confirmation-backdrop');

    if (!nevermindBtn || !confirmAbandonBtn || !confirmationBackdrop) {
      logger.error({
        [FIELDS.OPERATION]: 'setupAbandonConfirmation',
        [FIELDS.REASON]: 'dom_elements_missing'
      }, 'Confirmation popup elements not found in shadow DOM');
      return;
    }

    // Remove any existing listeners first
    if (this.confirmationHandlersSetup) {
      return; // Already set up
    }

    // Nevermind button - close popup without action
    nevermindBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hideAbandonConfirmation();
    });

    // Confirm abandon button - actually close the modal
    confirmAbandonBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hideAbandonConfirmation();
      this.close();
    });

    // Click backdrop to cancel (optional)
    confirmationBackdrop.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hideAbandonConfirmation();
    });

    this.confirmationHandlersSetup = true;
  }

  showAbandonConfirmation() {
    const confirmationPopup = this.shadowRoot.querySelector('#abandon-confirmation');

    // Set up handlers if not already done
    if (!this.confirmationHandlersSetup) {
      this.setupAbandonConfirmation();
    }

    confirmationPopup.style.display = 'flex';
  }

  hideAbandonConfirmation() {
    const confirmationPopup = this.shadowRoot.querySelector('#abandon-confirmation');
    confirmationPopup.style.display = 'none';
  }

  destroy() {
    
    // Remove elements
    if (this.container) {
      this.container.remove();
    }
    
    if (this.triggerButton) {
      this.triggerButton.remove();
    }

    // Reset state
    this.container = null;
    this.shadowRoot = null;
    this.triggerButton = null;
    this.isOpen = false;
  }
}

// Create global instance
const widget = new RoofScannerWidget();

// Extract configuration from script tag
const script = document.currentScript || document.querySelector('script[src*="roof-scanner"]');
const config = {
  mode: script?.getAttribute('data-mode') || undefined,
  position: script?.getAttribute('data-position') || undefined
  // serverUrl and googleMapsApiKey are built into the widget from environment variables
};

// Initialize widget asynchronously
widget.init(config).then(() => {
  // Expose public API after initialization is complete
  window.RoofScanner = {
    open: () => widget.open(),
    close: () => widget.close(),
    destroy: () => widget.destroy(),
    init: (newConfig) => widget.init(newConfig)
  };

  // Expose authenticated API helper for internal use
  // Automatically adds JWT token to all requests
  window.RoofScannerAPI = {
    async fetch(url, options = {}) {
      const token = window.ROOF_SCANNER_CONFIG?.widgetToken;

      if (!token) {
        logger.error({
          [FIELDS.OPERATION]: 'api_fetch',
          [FIELDS.REASON]: 'missing_token',
          [FIELDS.ENDPOINT]: url
        }, 'JWT token not available for API request');
        throw new Error('Widget not properly initialized - JWT token missing');
      }

      return fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
    }
  };

  // Dispatch ready event
  window.dispatchEvent(new CustomEvent('roofscanner:ready'));
}).catch(error => {
  logger.error({
    [FIELDS.ERROR]: error,
    [FIELDS.OPERATION]: 'init'
  }, 'Failed to initialize widget');
});

} // End of !window.__roofScannerLoaded check